import React, { useState, useEffect, useContext } from "react";
import {
  FiPlus,
  FiDownload,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
} from "react-icons/fi";
import axios from "axios";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";

const EmailTemplatesPage = () => {
  const { user } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    subject: "",
    body: "",
    type: "",
    attachment: null,
  });
  const [variables, setVariables] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Extract variables from template body
  useEffect(() => {
    const vars = [
      ...new Set(
        (formData.body.match(/\{\{(.*?)\}\}/g) || []).map((match) =>
          match.replace(/\{\{|\}\}/g, "")
        )
      ),
    ];
    setAvailableVariables(vars);
  }, [formData.body]);

  // Fetch templates from API

  const fetchTemplates = async () => {
    const companyId =
      user?.accountType === "COMPANY" ? user?.id : user?.companyId;
    try {
      const response = await axios.get(
        `${API_URL}/api/emailTemplates/company/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      setTemplates(response.data.emailTemplates);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      attachment: e.target.files[0],
    });
  };

  const addVariable = () => {
    const newVarName = `var${variables.length + 1}`;
    const newVar = { name: newVarName, value: "" };
    setVariables([...variables, newVar]);
    insertVariable(newVarName);
  };

  const insertVariable = (varName) => {
    if (!varName) return;
    const textarea = document.getElementById("body");
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const newText =
      formData.body.substring(0, startPos) +
      `{{${varName}}}` +
      formData.body.substring(endPos);

    setFormData({
      ...formData,
      body: newText,
    });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.selectionStart = startPos + varName.length + 4;
      textarea.selectionEnd = startPos + varName.length + 4;
      textarea.focus();
    }, 0);
  };

  const handleVariableValueChange = (varName, value) => {
    setVariables((vars) =>
      vars.map((v) => (v.name === varName ? { ...v, value } : v))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("body", formData.body);
      formDataToSend.append("type", formData.type);
      formDataToSend.append(
        "variables",
        JSON.stringify(
          availableVariables.map((name) => ({
            name,
            value: variables.find((v) => v.name === name)?.value || "",
          }))
        )
      );
      if (formData.attachment) {
        formDataToSend.append("file", formData.attachment);
      }
      formDataToSend.append(
        "companyId",
        user.accountType === "COMPANY" ? user?.id : user.companyId
      );

      let response;
      if (isEditMode) {
        response = await axios.put(
          `${API_URL}/api/emailTemplates/${currentTemplateId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        setTemplates(
          templates.map((template) =>
            template.emailTemplateId === currentTemplateId
              ? response.data
              : template
          )
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/emailTemplates`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        setTemplates([...templates, response.data]);
      }

      setIsModalOpen(false);
      setIsEditMode(false);
      fetchTemplates();
      // setCurrentTemplateId(null);
      // setFormData({
      //   subject: '',
      //   body: '',
      //   type: '',
      //   attachment: null
      // });
      setVariables([]);
        toast.success(response.data.message || "Template saved successfully");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to save template");
    }
  };

  const handleEdit = (template) => {
    setCurrentTemplateId(template.emailTemplateId);
    setIsEditMode(true);
    setFormData({
      subject: template.subject,
      body: template.body,
      type: template.type,
      attachment: null,
    });

    // Extract variables and their values
    const vars = [
      ...new Set(
        (template.body.match(/\{\{(.*?)\}\}/g) || []).map((match) =>
          match.replace(/\{\{|\}\}/g, "")
        )
      ),
    ];

    const varValues = vars.map((name) => ({
      name,
      value: template.variables?.find((v) => v.name === name)?.value || "",
    }));

    setVariables(varValues);
    setIsModalOpen(true);
  };

  const handleDownload = (attachmentUrl) => {
    window.open(attachmentUrl, "_blank");
  };

  const confirmDelete = (templateId) => {
    setTemplateToDelete(templateId);
    setIsDeleteConfirmOpen(true);
  };

  const deleteTemplate = async () => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/emailTemplates/${templateToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setTemplates(
        templates.filter(
          (template) => template.emailTemplateId !== templateToDelete
        )
      );
      fetchTemplates();
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data.message);
    } finally {
      setIsDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  // Preview with replaced variables
  const renderPreview = () => {
    return availableVariables.reduce((text, varName) => {
      const value =
        variables.find((v) => v.name === varName)?.value || `{{${varName}}}`;
      return text.replace(new RegExp(`\\{\\{${varName}\\}\\}`, "g"), value);
    }, formData.body);
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentTemplateId(null);
    setFormData({
      subject: "",
      body: "",
      type: "",
      attachment: null,
    });
    setVariables([]);
  };

  return (
    <div className="container px-2 sm:px-4 py-4 sm:py-8 bg-gray-100 shadow-lg dark:bg-gray-900 dark:text-white">
      {/* Header with plus button */}
      <div className="flex justify-between items-center mb-4 sm:mb-6 dark:bg-gray-900 dark:text-white">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
          Email Templates
        </h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white p-1 sm:p-2 rounded-full flex items-center"
        >
          <FiPlus size={18} className="sm:size-5" />
        </button>
      </div>

      {/* Modal for creating/editing template */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 sm:p-6 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg sm:max-w-2xl mx-auto p-4 sm:p-6 relative dark:bg-gray-800 dark:text-white">
            <button
              onClick={resetForm}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <FiX size={24} />
            </button>
            <div className="max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg sm:text-xl font-semibold mb-4">
                {isEditMode
                  ? "Edit Email Template"
                  : "Create New Email Template"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div>
                    <label
                      className="block text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base dark:text-white"
                      htmlFor="subject"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-sm sm:text-base dark:bg-gray-800 dark:text-white"
                      required
                    />
                  </div>
                </div>

                {/* Body with Variable Management */}
                <div className="mb-3 sm:mb-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-1 sm:mb-2 gap-2">
                    <label
                      className="block text-gray-700 text-sm sm:text-base dark:text-white"
                      htmlFor="body"
                    >
                      Body
                    </label>
                    <div className="flex justify-end sm:justify-normal">
                      <button
                        type="button"
                        onClick={addVariable}
                        className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 text-xs sm:text-sm"
                      >
                        <FiPlus className="inline mr-1" /> Add Variable
                      </button>
                    </div>
                  </div>
                  <textarea
                    id="body"
                    name="body"
                    value={formData.body}
                    onChange={handleInputChange}
                    className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-sm sm:text-base dark:bg-gray-800 dark:text-white"
                    rows="6"
                    required
                  />
                  <div className="mt-1 sm:mt-2 flex flex-wrap gap-1 sm:gap-2dark:bg-gray-800 dark:text-white">
                    {availableVariables.map((varName) => (
                      <button
                        key={varName}
                        type="button"
                        onClick={() => insertVariable(varName)}
                        className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-xs sm:text-sm"
                      >
                        {`{{${varName}}}`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Variable Values */}
                {availableVariables.length > 0 && (
                  <div className="mb-3 sm:mb-4 bg-gray-50 p-2 sm:p-4 rounded-md dark:bg-gray-800 dark:text-white">
                    <h3 className="text-md sm:text-lg font-medium text-gray-700 mb-2 sm:mb-3 dark:text-white">
                      Variable Values
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 dark:bg-gray-800 dark:text-white">
                      {availableVariables.map((varName) => (
                        <div key={varName} className="space-y-1 dark:bg-gray-800 dark:text-white">
                          <label className="block text-xs sm:text-sm text-gray-600 dark:text-white">{`{{${varName}}}`}</label>
                          <input
                            type="text"
                            value={
                              variables.find((v) => v.name === varName)
                                ?.value || ""
                            }
                            onChange={(e) =>
                              handleVariableValueChange(varName, e.target.value)
                            }
                            className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm dark:bg-gray-800 dark:text-white"
                            placeholder={`Value for {{${varName}}}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                <div className="mb-3 sm:mb-4 bg-gray-50 p-2 sm:p-4 rounded-md dark:bg-gray-800 dark:text-white">
                  <h3 className="text-md sm:text-lg font-medium text-gray-700 mb-1 sm:mb-2 dark:text-white">
                    Preview
                  </h3>
                  <div className="p-2 sm:p-3 bg-white border border-gray-200 rounded whitespace-pre-wrap min-h-16 sm:min-h-20 text-xs sm:text-sm dark:bg-gray-800 dark:text-white">
                    {renderPreview() || (
                      <span className="text-gray-400">
                        Preview will appear here
                      </span>
                    )}
                  </div>
                </div>

                {/* Type and Attachment */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 ">
                  <div>
                    <label
                      className="block text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base dark:text-white"
                      htmlFor="type"
                    >
                      Type
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-sm sm:text-base dark:bg-gray-800 dark:text-white"
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="marketing">Marketing</option>
                      <option value="transactional">Transactional</option>
                      <option value="notification">Notification</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base dark:text-white"
                      htmlFor="attachment"
                    >
                      Attachment
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-md p-2 sm:p-4 text-center">
                      <input
                        type="file"
                        id="attachment"
                        name="attachment"
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                      />
                      <label
                        htmlFor="attachment"
                        className="cursor-pointer text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
                      >
                        {formData.attachment ? (
                          <span className="truncate block">
                            {formData.attachment.name}
                          </span>
                        ) : (
                          <span>Click to upload (PDF, DOC, XLS)</span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 sm:space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-3 sm:px-4 py-1 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm sm:text-base dark:bg-gray-500 dark:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm sm:text-base flex items-center"
                  >
                    {isEditMode ? (
                      <>
                        <FiSave className="mr-1" /> Update Template
                      </>
                    ) : (
                      "Save Template"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Templates Table - Made responsive */}
      <div className="bg-white rounded-lg shadow overflow-hidden dark:bg-gray-800 dark:text-white">
        {isLoading ? (
          <div className="p-4 sm:p-8 text-center">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="p-4 sm:p-8 text-center text-gray-500">
            No templates found
          </div>
        ) : (
          <div className="overflow-x-auto dark:bg-gray-800 dark:text-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-800 dark:text-white">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    S.No.
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    Subject
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    Body Preview
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    Type
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    Attachment
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:text-white">
                {templates?.map((template, index) => (
                  <tr key={template?.emailTemplateId}>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-white">
                      {index + 1}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-white">
                      {template.subject}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-500 max-w-[100px] sm:max-w-xs">
                      <div className="truncate">
                        {(template.body || "").split("{{").map((part, i) => {
                          if (i === 0) return part;
                          const [varName, ...rest] = part.split("}}");
                          return (
                            <span key={i}>
                              <span className="bg-blue-100 text-blue-800 px-1 rounded">
                                {varName}
                              </span>
                              {rest.join("")}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-white">
                      {template.type}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-white">
                      {template.attachment ? (
                        <button
                          onClick={() => handleDownload(template.attachment)}
                          className="text-blue-600 hover:text-blue-800 flex items-center text-xs sm:text-sm"
                        >
                          <FiDownload className="mr-1" />
                          Download
                        </button>
                      ) : (
                        "No attachment"
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FiEdit2 size={14} className="sm:size-4" />
                        </button>
                        <button
                          onClick={() =>
                            confirmDelete(template.emailTemplateId)
                          }
                          className="cursor-pointer text-red-600 hover:text-red-800"
                        >
                          <FiTrash2 size={14} className="sm:size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isDeleteConfirmOpen && (
           <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white p-6 rounded-md shadow-xl w-full max-w-md dark:bg-gray-800 dark:text-white">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Confirm Delete
              </h2>
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this email template? This action
                cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteTemplate}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
